import type {
  Customer,
  CustomerStatus,
  EnvironmentKind,
  License,
  LogAccess,
  MaintenanceTerm,
  RenewalStatus,
  Topology,
  UpdateCustomerRequest,
} from '@central/api';
import { formatDate } from '@central/ui';
import {
  Alert,
  Button,
  Group,
  Modal,
  MultiSelect,
  Select,
  Switch,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent, type ReactNode } from 'react';
import { keys, useApi } from '../api';
import { CustomerSelect } from '../components/CustomerSelect';
import { errorMessage } from '../lib/errors';
import { fromDateInput, toDateInput } from '../lib/format';
import { renewalStatusLabel, statusMove } from '../lib/customer';
import { logAccessLabel } from '../lib/labels';
import { buildCreate, newDraft, validate, type Draft, type DraftErrors } from '../lib/license';

type Fields = Record<string, string> | undefined;
const fieldsOf = (e: unknown): Fields => (e as { fields?: Record<string, string> }).fields;

/** A dialog form: fields, the error from the server, Cancel and the submit button. */
export function Form({
  onSubmit,
  children,
  error,
  submitLabel,
  pending,
  onCancel,
}: {
  onSubmit: (e: FormEvent) => void;
  children: ReactNode;
  error?: unknown;
  submitLabel: string;
  pending: boolean;
  onCancel: () => void;
}) {
  return (
    <form
      onSubmit={onSubmit}
      noValidate
      style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
    >
      {children}
      {!!error && <Alert color="red">{errorMessage(error)}</Alert>}
      <Group justify="flex-end">
        <Button variant="default" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" color="dark" loading={pending}>
          {submitLabel}
        </Button>
      </Group>
    </form>
  );
}

/** PATCHes the customer and stores the answer. */
function useUpdateCustomer(c: Customer, onDone: () => void) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (b: UpdateCustomerRequest) => api.customers.update(c.id, b),
    onSuccess: async (next) => {
      queryClient.setQueryData(keys.customer(c.id), next);
      await queryClient.invalidateQueries({ queryKey: keys.customers });
      await queryClient.invalidateQueries({ queryKey: keys.overview });
      // A reseller's customer counts and summary move with the customer.
      if ((next.resellerId ?? '') !== (c.resellerId ?? ''))
        await queryClient.invalidateQueries({ queryKey: keys.resellers });
      onDone();
    },
  });
}

/**
 * Edit the maintenance term (replaced whole) and the renewal status; the
 * renewal is sent only when its status or note changed.
 */
export function MaintenanceModal({
  c,
  opened,
  onClose,
}: {
  c: Customer;
  opened: boolean;
  onClose: () => void;
}) {
  const m = c.maintenance;
  const [startsAt, setStartsAt] = useState(m.startsAt ? toDateInput(m.startsAt) : '');
  const [endsAt, setEndsAt] = useState(m.endsAt ? toDateInput(m.endsAt) : '');
  const [covers, setCovers] = useState(m.coversThrough ?? '');
  const [renewal, setRenewal] = useState<RenewalStatus>(m.renewal?.status ?? 'none');
  const [note, setNote] = useState(m.renewal?.note ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const save = useUpdateCustomer(c, onClose);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const v: Record<string, string> = {};
    if (startsAt && endsAt && endsAt < startsAt) v.endsAt = 'The end is before the start.';
    if (covers.trim() && !/^\d+\.\d+$/.test(covers.trim()))
      v.covers = 'A release line (MAJOR.MINOR), e.g. 1.4.';
    if ([...note.trim()].length > renewalNoteMax) v.note = `At most ${renewalNoteMax} characters.`;
    setErrors(v);
    if (Object.keys(v).length) return;
    const next: MaintenanceTerm = {};
    if (startsAt) next.startsAt = fromDateInput(startsAt);
    if (endsAt) next.endsAt = fromDateInput(endsAt);
    if (covers.trim()) next.coversThrough = covers.trim();
    const body: UpdateCustomerRequest = { maintenance: next };
    if (renewal !== (m.renewal?.status ?? 'none') || note.trim() !== (m.renewal?.note ?? '')) {
      body.renewal = { status: renewal };
      if (note.trim()) body.renewal.note = note.trim();
    }
    save.mutate(body);
  };
  return (
    <Modal opened={opened} onClose={onClose} title="Maintenance plan">
      <Form
        onSubmit={submit}
        error={save.error}
        submitLabel="Save plan"
        pending={save.isPending}
        onCancel={onClose}
      >
        <TextInput
          label="Starts"
          type="date"
          value={startsAt}
          onChange={(e) => setStartsAt(e.currentTarget.value)}
        />
        <TextInput
          label="Ends"
          type="date"
          value={endsAt}
          error={errors.endsAt}
          onChange={(e) => setEndsAt(e.currentTarget.value)}
        />
        <TextInput
          label="Covers releases through"
          placeholder="1.4"
          value={covers}
          error={errors.covers}
          onChange={(e) => setCovers(e.currentTarget.value)}
        />
        <Select
          label="Renewal"
          description={
            m.renewal && m.renewal.status !== 'none'
              ? `Set ${formatDate(m.renewal.at)}; the date moves when the status changes.`
              : 'The date is set when the status changes.'
          }
          data={(Object.keys(renewalStatusLabel) as RenewalStatus[]).map((k) => ({
            value: k,
            label: renewalStatusLabel[k],
          }))}
          value={renewal}
          allowDeselect={false}
          onChange={(v) => v && setRenewal(v as RenewalStatus)}
        />
        <Textarea
          label="Renewal note"
          placeholder="Quote 2027-114 sent to purchasing"
          autosize
          minRows={2}
          value={note}
          error={errors.note}
          onChange={(e) => setNote(e.currentTarget.value)}
        />
      </Form>
    </Modal>
  );
}

/** The longest renewal note the server accepts, in characters. */
const renewalNoteMax = 500;

/**
 * Rename the customer and set or clear its account manager and its tier 1
 * reseller; only changed keys are sent.
 */
export function CustomerDetailsModal({ c, onClose }: { c: Customer; onClose: () => void }) {
  const api = useApi();
  const [name, setName] = useState(c.name);
  const [manager, setManager] = useState(c.accountManagerId ?? '');
  const [reseller, setReseller] = useState(c.resellerId ?? '');
  const [nameError, setNameError] = useState<string>();
  const staff = useQuery({ queryKey: keys.staff, queryFn: () => api.staff.list() });
  const resellers = useQuery({ queryKey: keys.resellers, queryFn: () => api.resellers.list() });
  const save = useUpdateCustomer(c, onClose);
  const resellerOptions = [
    { value: '', label: 'None: Rethink supports them directly' },
    ...(resellers.data?.items ?? []).map((r) => ({ value: r.id, label: r.name })),
  ];
  const options = [
    { value: '', label: 'No account manager' },
    ...(staff.data?.items ?? [])
      .filter((u) => u.role !== 'Reseller')
      .filter((u) => u.enabled || u.id === c.accountManagerId)
      .map((u) => ({ value: u.id, label: u.name || u.email })),
  ];
  if (c.accountManagerId && !options.some((o) => o.value === c.accountManagerId))
    options.push({ value: c.accountManagerId, label: c.accountManager?.name ?? 'Current manager' });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return setNameError('Name the customer.');
    setNameError(undefined);
    const body: UpdateCustomerRequest = {};
    if (name.trim() !== c.name) body.name = name.trim();
    if (manager !== (c.accountManagerId ?? '')) body.accountManagerId = manager;
    if (reseller !== (c.resellerId ?? '')) body.resellerId = reseller;
    if (Object.keys(body).length === 0) return onClose();
    save.mutate(body);
  };
  return (
    <Modal opened onClose={onClose} title="Customer details">
      <Form
        onSubmit={submit}
        error={save.error}
        submitLabel="Save details"
        pending={save.isPending}
        onCancel={onClose}
      >
        <TextInput
          label="Name"
          data-autofocus
          value={name}
          error={nameError}
          onChange={(e) => setName(e.currentTarget.value)}
        />
        <Select
          label="Account manager"
          data={options}
          value={manager}
          allowDeselect={false}
          disabled={staff.isPending}
          onChange={(v) => setManager(v ?? '')}
        />
        <Select
          label="Reseller"
          description="Gives tier 1 support and installs; sees no licenses. Escalates to Rethink."
          data={resellerOptions}
          value={reseller}
          allowDeselect={false}
          disabled={resellers.isPending}
          onChange={(v) => setReseller(v ?? '')}
        />
      </Form>
    </Modal>
  );
}

/** Confirms a status change, saying what it does and does not change. */
export function CustomerStatusModal({
  c,
  to,
  onClose,
}: {
  c: Customer;
  to: CustomerStatus;
  onClose: () => void;
}) {
  const save = useUpdateCustomer(c, onClose);
  const move = statusMove[to];
  return (
    <Modal opened onClose={onClose} title={`${move.verb}?`}>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate({ status: to });
        }}
        error={save.error}
        submitLabel={move.verb}
        pending={save.isPending}
        onCancel={onClose}
      >
        <Text size="sm">
          <strong>{c.name}</strong>. {move.effect}
        </Text>
      </Form>
    </Modal>
  );
}

/** Edit the portal settings staff manage; only changed keys are sent. */
export function SettingsModal({
  c,
  opened,
  onClose,
}: {
  c: Customer;
  opened: boolean;
  onClose: () => void;
}) {
  const [requireMfa, setRequireMfa] = useState(c.settings.requireMfa);
  const [logAccess, setLogAccess] = useState<LogAccess>(c.settings.logAccess);
  const [healthEmails, setHealthEmails] = useState(c.healthEmails);
  const save = useUpdateCustomer(c, onClose);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const settings: UpdateCustomerRequest['settings'] = {};
    if (requireMfa !== c.settings.requireMfa) settings.requireMfa = requireMfa;
    if (logAccess !== c.settings.logAccess) settings.logAccess = logAccess;
    const body: UpdateCustomerRequest = {};
    if (Object.keys(settings).length > 0) body.settings = settings;
    if (healthEmails !== c.healthEmails) body.healthEmails = healthEmails;
    if (Object.keys(body).length === 0) return onClose();
    save.mutate(body);
  };
  return (
    <Modal opened={opened} onClose={onClose} title="Portal settings">
      <Form
        onSubmit={submit}
        error={save.error}
        submitLabel="Save settings"
        pending={save.isPending}
        onCancel={onClose}
      >
        <Switch
          label="Require MFA for every user"
          checked={requireMfa}
          onChange={(e) => setRequireMfa(e.currentTarget.checked)}
        />
        <Select
          label="Who can view logs"
          data={(Object.keys(logAccessLabel) as LogAccess[]).map((k) => ({
            value: k,
            label: logAccessLabel[k],
          }))}
          value={logAccess}
          allowDeselect={false}
          onChange={(v) => v && setLogAccess(v as LogAccess)}
        />
        <Switch
          label="Email deployment health alerts to this customer"
          description="Staff always get them. Customer users get them only for deployments that show incidents, and can turn them off in their notification settings."
          checked={healthEmails}
          onChange={(e) => setHealthEmails(e.currentTarget.checked)}
        />
      </Form>
    </Modal>
  );
}

/**
 * A new environment with one tenant bound to a customer: `c`, or the one
 * chosen in the dialog when `c` is not given.
 */
export function NewEnvironmentModal({
  c,
  opened,
  onClose,
}: {
  c?: Pick<Customer, 'id' | 'name'>;
  opened: boolean;
  onClose: () => void;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [chosen, setChosen] = useState('');
  const customerId = c?.id ?? chosen;
  const [name, setName] = useState('');
  const [kind, setKind] = useState<EnvironmentKind>('dedicated');
  const [topology, setTopology] = useState<Topology | ''>('');
  const [tenantId, setTenantId] = useState('');
  const [licenseId, setLicenseId] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Every page, so any of the customer's licenses can be bound.
  const licenses = useQuery({
    queryKey: [...keys.licenseList(customerId), 'all'],
    queryFn: async () => {
      const all: License[] = [];
      let cursor: string | undefined;
      do {
        const page = await api.licenses.list({ customerId, cursor });
        all.push(...page.items);
        cursor = page.nextCursor;
      } while (cursor);
      return all;
    },
    enabled: opened && !!customerId,
  });
  const create = useMutation({
    mutationFn: () =>
      api.environments.create({
        name: name.trim(),
        kind,
        topology: topology || undefined,
        tenants: [{ tenantId: tenantId.trim(), customerId, licenseId: licenseId || undefined }],
      }),
    onSuccess: async (env) => {
      queryClient.setQueryData(keys.environment(env.id), env);
      await queryClient.invalidateQueries({ queryKey: keys.environmentLists });
      await queryClient.invalidateQueries({ queryKey: keys.overview });
      await navigate({ to: '/environments/$environmentId', params: { environmentId: env.id } });
    },
    onError: (e) => {
      const f = fieldsOf(e);
      if (f?.name) setErrors((x) => ({ ...x, name: 'Name the environment.' }));
      if (f?.tenants) setErrors((x) => ({ ...x, tenantId: f.tenants! }));
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const v: Record<string, string> = {};
    if (!customerId) v.customerId = 'Choose the customer.';
    if (!name.trim()) v.name = 'Name the environment.';
    if (!tenantId.trim()) v.tenantId = 'Enter the GoATMS tenant id.';
    setErrors(v);
    if (Object.keys(v).length === 0) create.mutate();
  };
  const fieldErr = Object.keys(errors).length > 0;
  return (
    <Modal opened={opened} onClose={onClose} title="New environment">
      <Form
        onSubmit={submit}
        error={fieldErr ? undefined : create.error}
        submitLabel="Create environment"
        pending={create.isPending}
        onCancel={onClose}
      >
        {!c && (
          <CustomerSelect
            label="Customer"
            placeholder="Choose a customer"
            value={chosen}
            error={errors.customerId}
            onChange={(id) => {
              setChosen(id);
              setLicenseId('');
            }}
          />
        )}
        <TextInput
          label="Name"
          placeholder="Production"
          data-autofocus
          value={name}
          error={errors.name}
          onChange={(e) => setName(e.currentTarget.value)}
        />
        <Select
          label="Type"
          data={[
            { value: 'dedicated', label: 'Dedicated · one customer' },
            { value: 'shared', label: 'Shared · several customers' },
          ]}
          value={kind}
          allowDeselect={false}
          onChange={(v) => v && setKind(v as EnvironmentKind)}
        />
        <Select
          label="Topology"
          placeholder="Reported at enrollment"
          clearable
          data={[
            { value: 'kubernetes', label: 'Kubernetes' },
            { value: 'compose', label: 'Compose' },
            { value: 'standalone', label: 'Standalone' },
          ]}
          value={topology || null}
          onChange={(v) => setTopology((v ?? '') as Topology | '')}
        />
        <TextInput
          label="Tenant id"
          description={`The GoATMS tenant that belongs to ${c?.name ?? 'the customer'}`}
          value={tenantId}
          error={errors.tenantId}
          onChange={(e) => setTenantId(e.currentTarget.value)}
        />
        <Select
          label="License"
          placeholder="Bind later"
          clearable
          data={(licenses.data ?? []).map((l) => ({
            value: l.id,
            label: `${l.key} · to ${formatDate(l.expiresAt)}`,
          }))}
          value={licenseId || null}
          onChange={(v) => setLicenseId(v ?? '')}
        />
      </Form>
    </Modal>
  );
}

/**
 * A new license; features and seats are set in the editor next. Without
 * customerId the dialog asks for the customer first.
 */
export function NewLicenseModal({
  customerId,
  opened,
  onClose,
}: {
  customerId?: string;
  opened: boolean;
  onClose: () => void;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [draft, setDraft] = useState<Draft>(() => newDraft());
  const [errors, setErrors] = useState<DraftErrors>({});
  const [picked, setPicked] = useState('');
  const [customerError, setCustomerError] = useState<string>();
  const forCustomer = customerId ?? picked;
  const products = useQuery({
    queryKey: keys.products,
    queryFn: () => api.catalogue.products(),
    enabled: opened,
  });
  const create = useMutation({
    mutationFn: () => api.licenses.create(buildCreate(forCustomer, draft)),
    onSuccess: async (l) => {
      queryClient.setQueryData(keys.license(l.id), l);
      await queryClient.invalidateQueries({ queryKey: keys.licenses });
      await queryClient.invalidateQueries({ queryKey: keys.customers });
      await navigate({ to: '/licenses/$licenseId', params: { licenseId: l.id } });
    },
  });
  const set = (p: Partial<Draft>) => setDraft((d) => ({ ...d, ...p }));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const v = validate(draft);
    if (draft.products.length === 0) v.products = 'Choose at least one product.';
    setErrors(v);
    const noCustomer = !forCustomer ? 'Choose the customer.' : undefined;
    setCustomerError(noCustomer);
    if (Object.keys(v).length === 0 && !noCustomer) create.mutate();
  };
  return (
    <Modal opened={opened} onClose={onClose} title="New license">
      <Form
        onSubmit={submit}
        error={create.error}
        submitLabel="Create license"
        pending={create.isPending}
        onCancel={onClose}
      >
        {customerId === undefined && (
          <CustomerSelect
            label="Customer"
            placeholder="Choose a customer"
            value={picked}
            error={customerError}
            onChange={(id) => {
              setPicked(id);
              setCustomerError(undefined);
            }}
          />
        )}
        <Group grow align="flex-start">
          <TextInput
            label="Starts"
            type="date"
            value={draft.startsAt}
            error={errors.startsAt}
            onChange={(e) => set({ startsAt: e.currentTarget.value })}
          />
          <TextInput
            label="Expires"
            type="date"
            value={draft.expiresAt}
            error={errors.expiresAt}
            onChange={(e) => set({ expiresAt: e.currentTarget.value })}
          />
        </Group>
        <Group grow align="flex-start">
          <TextInput
            label="Grace after expiry (days)"
            inputMode="numeric"
            value={draft.graceDays}
            error={errors.graceDays}
            onChange={(e) => set({ graceDays: e.currentTarget.value })}
          />
          <TextInput
            label="Allowed activations"
            inputMode="numeric"
            description="−1 unlimited · 0 offline only"
            value={draft.allowedActivations}
            error={errors.allowedActivations}
            onChange={(e) => set({ allowedActivations: e.currentTarget.value })}
          />
        </Group>
        <MultiSelect
          label="Products"
          description="Turn on features and set seats in the license editor next."
          data={(products.data?.items ?? []).map((p) => ({ value: p.id, label: p.name }))}
          value={draft.products.map((p) => p.productId)}
          error={errors.products}
          onChange={(ids) =>
            set({ products: ids.map((productId) => ({ productId, features: [] })) })
          }
        />
      </Form>
    </Modal>
  );
}
